import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import type { VoidFunctionComponent } from 'react';

import TextArea from '../../../../generic/Forms/TextArea';
import { icons, IconText } from '../../../../generic/Icon';
import Loader from '../../../../generic/Loader';
import Tabs, { useTabs } from '../../../../generic/Tabs';

import StatusBadge from './StatusBadge';
import { badgeTypes, formatTypes } from './types';
import { badgeFormatToLabel, BadgeTypes, getBadgeRenderText } from './utils';
import type { BadgeBaseProps, BadgeFormat, BadgeType } from './types';

import styles from './BadgePanel.module.less';

type Props = BadgeBaseProps & {
  /**
   * Security is enabled for the given project
   */
  isSecurityEnabled: boolean;
  /**
   * Default tab to be selected on open
   */
  defaultTab?: BadgeType;
};

const BadgePanel: VoidFunctionComponent<Props> = ({
  locator,
  isSecurityEnabled,
  defaultTab = 'licenseShield',
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasBeenCopied, setHasBeenCopied] = useState<boolean>(false);

  const availableBadgeTypes = useMemo(() =>
    badgeTypes.filter(([key]) => (key === 'securityShield' ? isSecurityEnabled : true)), [isSecurityEnabled]);

  const {
    tabs: badgeTypeTabs,
    activeTab: activeBadgeTypeTab,
  } = useTabs<BadgeType>({ tabIds: availableBadgeTypes, defaultTab });
  const {
    tabs: badgeFormatTabs,
    activeTab: activeBadgeFormatTab,
  } = useTabs<BadgeFormat>({ tabIds: [...formatTypes] });

  const badgeInfo = BadgeTypes[activeBadgeTypeTab];
  const badgeRenderContent = getBadgeRenderText(
    locator,
    activeBadgeFormatTab,
    badgeInfo.badgeType,
    badgeInfo.issueType,
  );

  //  Reset the loading state when the badge type changes.
  useEffect(() => {
    let timer: number;
    if (hasBeenCopied) {
      timer = window.setTimeout(() => setHasBeenCopied(false), 1000);
    }
    return () => clearTimeout(timer);
  }, [hasBeenCopied]);

  return (
    <div className={styles.badgePanel}>
      <h4 className={styles.badgePanelHeader}>
        Embed Status Badge
      </h4>
      <Tabs>
        {badgeTypeTabs.map((tab) => {
          const { title } = BadgeTypes[tab.label] || { title: tab.label };
          return (
            <Tabs.Tab
              key={tab.label}
              {...tab}
              label={title}
              onSelect={() => {
                //  Ensure that we setIsLoading to true when the tab is selected.
                setIsLoading(true);
                tab.onSelect();
              }}
            />
          );
        })}
      </Tabs>
      {isLoading && (
        <div className={styles.loader}>
          <Loader size="23" />
        </div>
      )}
      <div className={cx(styles.badgeImage, {
        [styles.badgeImageLarge]: activeBadgeTypeTab === 'large',
        //  Note: this is being visually hidden because we rely on the onLoad event to trigger the loading state.
        hidden: isLoading,
      })}>
        <StatusBadge
          locator={locator}
          badgeType={activeBadgeTypeTab}
          onLoad={() => setIsLoading(false)}
        />
      </div>
      <Tabs>
        {badgeFormatTabs.map((tab) => (
          <Tabs.Tab
            key={tab.label}
            {...tab}
            label={badgeFormatToLabel[tab.label]}
          />
        ))}
      </Tabs>
      <CopyToClipboard
        text={badgeRenderContent}
        onCopy={() => setHasBeenCopied(true)}>
        <div className={styles.badgeLink}>
          <b className="inline-header">
            <IconText
              icon={icons.clipboard}
              text={!hasBeenCopied ? 'Click to Copy:' : 'Copied!'}
            />
          </b>
          <TextArea
            label={!hasBeenCopied ? 'Click to Copy:' : 'Copied!'}
            value={badgeRenderContent}
            size="small"
            shouldSelectTextOnFocus
          />
        </div>
      </CopyToClipboard>
    </div>
  );
};

export default BadgePanel;
